import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { ThemeStateService } from '@sites/site-theme/src/state';
import { type ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import { CSS_RESTRICTIONS } from '@common/constants';
import { ELineStyle } from '@common/enums';
import type { FilterFormType, FilterGeneralFormType } from '@web/widgets/catalog-products/type';
import { CommonModule } from '@angular/common';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';

@Component({
    selector: 'web-filter-general-settings',
    templateUrl: './filter-general-settings.component.html',
    styleUrls: ['./filter-general-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, CommonElementsModule],
})
export class FilterGeneralSettingsComponent {
    @Input() generalFormGroup: FilterFormType['general'];

    private readonly themeState = inject(ThemeStateService);

    public readonly selectedThemeColors = this.themeState.selectedColors;

    public readonly lineStyle = ELineStyle;

    public setThemeBackgroundColor({ value, presetName }: ColorPickerEventPayload): void {
        this.generalFormGroup.markAsTouched({ onlySelf: true });

        this.backgroundColor.patchValue(presetName ?? value);
    }

    public setBorderRadius(value: number): void {
        this.generalFormGroup.markAsTouched({ onlySelf: true });

        this.borderRadius.patchValue(value);
    }

    public setBorderWidth(value: number): void {
        this.borderWidth.patchValue(value);
    }

    public setBorderColor({ value }: ColorPickerEventPayload): void {
        this.borderColor.patchValue(value);
    }

    public setPaddingTop(value: number): void {
        this.paddingTop.setValue(value);
    }

    public setPaddingBottom(value: number): void {
        this.paddingBottom.setValue(value);
    }

    public setPaddingLeft(value: number): void {
        this.paddingLeft.setValue(value);
    }

    public setPaddingRight(value: number): void {
        this.paddingRight.setValue(value);
    }

    public setAllPaddings(value: number): void {
        this.generalFormGroup.patchValue({
            paddingTop: value,
            paddingBottom: value,
            paddingLeft: value,
            paddingRight: value,
        });
    }

    public get BORDER_RADIUS_MIN(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MIN;
    }

    public get BORDER_RADIUS_MAX(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MAX;
    }

    public get SIZE_MIN(): number {
        return CSS_RESTRICTIONS.SIZE_MIN;
    }

    public get SIZE_MAX(): number {
        return CSS_RESTRICTIONS.SIZE_MAX;
    }

    public get backgroundColor(): FilterGeneralFormType['backgroundColor'] {
        return this.generalFormGroup.get('backgroundColor') as FilterGeneralFormType['backgroundColor'];
    }

    public get borderRadius(): FilterGeneralFormType['borderRadius'] {
        return this.generalFormGroup.get('borderRadius') as FilterGeneralFormType['borderRadius'];
    }

    public get isBorder(): FilterGeneralFormType['isBorder'] {
        return this.generalFormGroup.get('isBorder') as FilterGeneralFormType['isBorder'];
    }

    public get borderWidth(): FilterGeneralFormType['borderWidth'] {
        return this.generalFormGroup.get('borderWidth') as FilterGeneralFormType['borderWidth'];
    }

    public get borderStyle(): FilterGeneralFormType['borderStyle'] {
        return this.generalFormGroup.get('borderStyle') as FilterGeneralFormType['borderStyle'];
    }

    public get borderColor(): FilterGeneralFormType['borderColor'] {
        return this.generalFormGroup.get('borderColor') as FilterGeneralFormType['borderColor'];
    }

    public get paddingTop(): FilterGeneralFormType['paddingTop'] {
        return this.generalFormGroup.get('paddingTop') as FilterGeneralFormType['paddingTop'];
    }

    public get paddingBottom(): FilterGeneralFormType['paddingBottom'] {
        return this.generalFormGroup.get('paddingBottom') as FilterGeneralFormType['paddingBottom'];
    }

    public get paddingLeft(): FilterGeneralFormType['paddingLeft'] {
        return this.generalFormGroup.get('paddingLeft') as FilterGeneralFormType['paddingLeft'];
    }

    public get paddingRight(): FilterGeneralFormType['paddingRight'] {
        return this.generalFormGroup.get('paddingRight') as FilterGeneralFormType['paddingRight'];
    }
}
